import React from 'react';
import Styled from 'styled';
import { useQuery } from '@apollo/client/react/hooks';
import { FormattedMessage } from 'react-intl';
import queryString from 'query-string';
import { useLocation } from 'react-router';

import ScrollToTop from 'lib/scroll/scroll-to-top';

import LoadingWrapperView from 'four-nets/ui/loaders/loading-wrapper-view';
import PagerView from 'four-nets/ui/pager-view';

import ProductsInfoBarView from '../product-lists/products-info-bar-view';
import ProductsGrid from './products-grid/products-grid';
import query, { ProductsESQuery, ProductsESQueryVariables } from './products.graphql';
import defaultStyles from './products.scss';

export interface Props {
    styles: typeof defaultStyles;
    categorySlug?: string;
    homepage?: boolean;
    hasActiveFilters?: boolean;
}

const ProductsElasticSearch: React.FC<Props> = React.memo(props => {
    const location = useLocation();
    const search = queryString.parse(location.search);

    const { styles, categorySlug, homepage, hasActiveFilters } = props;

    const { county, price, page, orderBy, excludeUsed, excludeVendor, search: searchQS, ...rest } = search;
    const countyNames = county
        ? typeof county === 'string'
            ? [county]
            : county.map((name: string) => name)
        : undefined;
    const [priceFrom, priceTo] = price
        ? price
              .split('-')
              .map((e: string) => parseFloat(e))
              .filter((e: number | null | undefined | string) => e != null && e !== '')
        : [];
    const properties = Object.keys(rest).map(key => ({ property: key, value: rest[key] }));

    const { loading, data } = useQuery<ProductsESQuery, ProductsESQueryVariables>(query, {
        variables: {
            categorySlug,
            countyNames,
            priceFrom,
            priceTo,
            properties,
            orderBy,
            page,
            excludeUsed,
            excludeVendor,
            search: searchQS,
        },
        fetchPolicy: 'cache-first',
    });

    return (
        <LoadingWrapperView loading={loading}>
            <ScrollToTop scrollOnMount={false} page={page} />
            <ProductsInfoBarView
                productsCount={(data && data.products && data.products.length) || 0}
                orderBy={orderBy}
                hasActiveFilters={hasActiveFilters}
            />
            <div className={styles.pageWrapper}>
                <ProductsGrid interscroller={!homepage} edges={data && data.products && data.products.edges} />
                {!(data && data.products && data.products.edges.length) ? (
                    <div className={styles.empty}>
                        <FormattedMessage id='BAZAAR_NO_PRODUCTS' />
                    </div>
                ) : null}
            </div>
            <div className={styles.pagerWrapper}>
                <PagerView count={data && data.products && data.products.totalPages} current={Number(page) || 1} />
            </div>
        </LoadingWrapperView>
    );
});

export default Styled(defaultStyles)(ProductsElasticSearch);
